import { flow, getRoot, types } from 'mobx-state-tree'
import { GameResult } from '../game/GameResult'
import { ContentMetadata } from '../UserQuiz'
import { GameQuestion } from './GameQuestion'
import { GameApi } from '../../services/api-objects'
import { InfoCard } from '../card/InfoCard'
import { DateTime } from 'luxon'

import { getType, Instance, isAlive, isStateTreeNode } from 'mobx-state-tree'

export const isInfoCard = (item: any): item is Instance<typeof InfoCard> => {
  return item && isStateTreeNode(item) && getType(item).name === 'InfoCard'
}
export const isGameQuestion = (item: any): item is Instance<typeof GameQuestion> => {
  return item && isStateTreeNode(item) && getType(item).name === 'GameQuestion'
}

export const GameQuiz = types
  .model('GameQuiz')
  .props({
    status: types.optional(
      types.enumeration([
        'idle',
        'pending',
        'pending-adaptive',
        'submitting',
        'done',
        'error',
        'ready',
      ]),
      'idle',
    ),
    assignment_id: types.maybeNull(types.number),
    result: GameResult,
    title: types.string,
    id: types.identifierNumber,
    metadata: types.maybeNull(ContentMetadata),
    test: types.boolean,
    reinforcement: types.maybe(types.boolean),
    items: types.array(types.union(GameQuestion, InfoCard)),
    activeItem: types.maybe(types.union(types.reference(GameQuestion), types.reference(InfoCard))),
    completed: types.maybe(types.boolean),
    tutorial: types.maybe(types.boolean),
  })
  .actions((self) => ({
    setStatus(value?: 'idle' | 'pending' | 'done' | 'error' | 'submitting') {
      self.status = value || 'idle'
    },
    setActiveItem(reference: typeof self.activeItem) {
      self.activeItem = reference
    },
    setCompleted(completed: boolean) {
      self.completed = completed
    },
    setTutorial(tutorial) {
      self.tutorial = tutorial
    },
  }))
  .views((self) => ({
    get loading() {
      return self.status === 'pending'
    },
    get activeIdx() {
      if (self.activeItem) {
        return self.items.indexOf(self.activeItem)
      }
      return 0
    },
    get tutorialAnswers() {
      return self.items.map((item) => {
        if (isGameQuestion(item)) {
          const { answeredCorrectly, text, selected, choices } = item
          return {
            correct: answeredCorrectly,
            question_text: text,
            question_answer_choices: choices.map((c) => ({
              choice_id: c.id,
              choice_text: c.text,
              correct: c.correct,
            })),
            user_answer_choices: selected.map((s) => ({ choice_id: s.id })),
          }
        }
      })
    },
    get answers() {
      const cards = [] as any
      const answers = [] as any
      self.items.map((item) => {
        if (isGameQuestion(item)) {
          const {
            id,
            answered,
            answer_time,
            question_type,
            answeredCorrectly,
            userMatching,
            userOrdering,
            userText,
            choices,
          } = item

          if (answered) {
            if (question_type === 'Matching') {
              answers.push({
                answer_time,
                question_id: id,
                choices: answeredCorrectly ? [{ id: choices[0].id }] : [{ pairs: userMatching }],
                question_type,
              })
            } else if (question_type === 'Ordering') {
              answers.push({
                answer_time,
                question_id: id,
                choices: answeredCorrectly ? [{ id: choices[0].id }] : [{ ordering: userOrdering }],
                question_type,
              })
            } else if (question_type === 'Open Text') {
              let selected
              if (answeredCorrectly) {
                const correct = choices.find(
                  (c) => c?.text?.toLowerCase() === userText?.toLowerCase() && c.correct,
                )
                selected = [{ id: correct!.id }]
              } else {
                selected = [{ text: userText }]
              }
              answers.push({
                answer_time,
                question_id: id,
                choices: selected,
                question_type,
              })
            } else {
              // MC, MCA, TF - choices is an array of user selected choice ids
              answers.push({
                answer_time,
                question_id: id,
                choices: choices
                  .filter((choice) => choice.selected)
                  .map((choice) => ({ id: choice.id })),
                question_type,
              })
            }
          }
        } else if (isInfoCard(item)) {
          const { id, start_time, end_time } = item
          if (start_time && end_time) {
            cards.push({ id, view_start: start_time, view_end: end_time })
          }
        }
      })
      return { questions: answers, cards }
    },
    get rootStore(): any {
      return getRoot(self)
    },
  }))
  // Handle for the pending question-advance timer (see `next`), kept off the
  // snapshot so it can be cleared on unmount without persisting.
  .volatile(() => ({
    advanceTimer: undefined as ReturnType<typeof setTimeout> | undefined,
  }))
  .actions((self) => ({
    afterCreate() {
      self.activeItem = self.items[0]
    },
    reset() {
      self.items.map((item) => {
        if (isGameQuestion(item)) {
          item.reset()
        }
      })
      self.activeItem = self.items[0]
    },
    // Cancel a pending advance so a torn-down screen can't mutate the tree.
    cancelAdvance() {
      if (self.advanceTimer) {
        clearTimeout(self.advanceTimer)
        self.advanceTimer = undefined
      }
    },
    // Runs when the advance timer fires. Guarded against a quiz tree that has been
    // detached (e.g. the user exited the quiz within the delay).
    advance(active) {
      self.advanceTimer = undefined
      if (!isAlive(self)) {
        return
      }
      const idx = self.items.indexOf(active)
      // Continue to next question if available, otherwise submit / mark complete
      if (self.items[idx + 1]) {
        self.setActiveItem(self.items[idx + 1])
        if (self.activeItem && isInfoCard(self.activeItem)) {
          self.activeItem.setStartTime(DateTime.now().toUTC().toISO())
        } else if (self.activeItem && isGameQuestion(active)) {
          self.activeItem.setStartTime(Date.now())
        }
      } else if (self.answers) {
        this.submitAnswers().then(() => {
          if (isAlive(self)) {
            self.setCompleted(true)
          }
        })
      }
    },
    next(fromFactoid) {
      const active = self.activeItem
      if (active) {
        if (isGameQuestion(active)) {
          // Update answer_time before continuing
          if (active.start_time) {
            active.setAnswerTime(Math.abs(Date.now() - active.start_time))
          } else {
            active.setAnswerTime(5000)
          }
        } else if (isInfoCard(active)) {
          active.setEndTime(DateTime.now().toUTC().toISO())
        }
        // After 2 seconds, move to the next question (0 when coming from feedback).
        // Replace any in-flight timer so rapid taps can't enqueue overlapping advances.
        this.cancelAdvance()
        self.advanceTimer = setTimeout(() => this.advance(active), fromFactoid ? 0 : 2000)
      }
    },
    submitAnswers: flow(function* () {
      self.setStatus('pending')
      try {
        if (self.tutorial) {
          self.setStatus('done')
          return
        }
        const answers = self.answers.questions.filter((a) => a !== null && a !== undefined)
        const cards = self.answers.cards

        self.setStatus('submitting')

        if (answers.length || cards.length) {
          const result = yield GameApi.submitAnswers(
            self.metadata?.subscription_user_id
              ? {
                  answers,
                  cards,
                  subscription_user_id: self.metadata.subscription_user_id,
                  content_user_id: self.metadata.content_user_id,
                  path_id: self.metadata.path_id,
                  topic_id: self.metadata.topic_id,
                  result_id: self.result.id,
                }
              : {
                  answers,
                  cards,
                  assignment_id: self.assignment_id,
                  content_user_id: self.metadata?.content_user_id,
                  path_id: self.metadata?.path_id,
                  quiz_id: self.metadata?.quiz_id,
                  topic_id: self.metadata?.topic_id,
                  result_id: self.result.id,
                },
          )

          if (result.ok) {
            self.setStatus('done')
          } else {
            self.setStatus('error')
          }
        } else {
          self.setStatus('error')
        }
      } catch (e) {
        self.setStatus('error')
      }
    }),
  }))
